/**
 * Mobile Payment Link Service
 *
 * Handles creation and delivery of payment links via SMS
 * Integrates with Stripe for payment processing and SMS services for delivery
 */

import { createPaymentLink, PaymentLinkRequest } from './stripePaymentService';
import { auth } from '../config/firebase';
// DURABLE DELIVERY + THREADING (Jose 2026-10-31): route the pay-link text through the SLIK Connect queue
// so it (a) survives flaky WiFi, (b) threads in the inbox under the "Payment link" chip, and (c) live-updates
// to sent/delivered/failed — closing the "no delivered confirmation" gap. The old direct /sendSms fetch
// gave no delivery status and never threaded. The paid confirmation is written by stripeWebhook.
import { communicationService } from './communicationService';

export interface MobilePaymentRequest {
  customerPhone: string;
  customerName: string;
  customerEmail?: string;
  items: Array<{
    name: string;
    price: number;
    quantity: number;
    description?: string;
  }>;
  total: number;
  tip?: number;
  tax?: number;
  metadata?: Record<string, string>;
}

export interface MobilePaymentResponse {
  success: boolean;
  paymentLink?: string;
  sessionId?: string;
  smsSent?: boolean;
  error?: string;
}

/**
 * Format phone number to E.164 format (required by SMS services)
 * Example: (555) 123-4567 -> +15551234567
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Add +1 for US numbers if not present
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }

  // If already has country code
  if (cleaned.length > 11) {
    return `+${cleaned}`;
  }

  return `+1${cleaned}`;
};

/**
 * Validate phone number format
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10 && cleaned.length <= 15;
};

/**
 * Send the payment link via SMS through the sendSms Cloud Function (Twilio creds server-side).
 * No longer a mock (Jose 2026-08-22). If Twilio isn't configured yet, this returns a soft failure
 * and the caller falls back to sharing the link manually (QR / copy) — the link still works.
 */
// Derive from the ACTIVE project id so a STAGING build hits STAGING functions (no real text), not PROD.
// This path sends SMS directly (bypassing smsService's staging guard), so env-scope it the same way. (Jose 2026-10-29)
const SMS_PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'slik-pos-barberlegacy';
const SMS_FUNCTIONS_URL = `https://us-central1-${SMS_PROJECT_ID}.cloudfunctions.net`;

const sendSMS = async (
  phone: string,
  message: string
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    const idToken = await auth.currentUser?.getIdToken().catch(() => undefined);
    const response = await fetch(`${SMS_FUNCTIONS_URL}/sendSms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
      body: JSON.stringify({ to: phone, body: message }),
    });
    const data = await response.json().catch(() => ({} as any));
    if (response.ok && data.success) {
      return { success: true, messageId: data.sid };
    }
    return { success: false, error: data.message || data.error || 'Failed to send SMS' };
  } catch (error: any) {
    console.error('Error sending SMS:', error);
    return { success: false, error: error.message || 'Failed to send SMS' };
  }
};

/**
 * Create a formatted SMS message for payment link
 */
const createPaymentMessage = (
  businessName: string,
  customerName: string,
  amount: number,
  paymentLink: string
): string => {
  return `Hi ${customerName}! Your ${businessName} service total is $${amount.toFixed(2)}. Pay securely here: ${paymentLink}`;
};

/**
 * Create and send a mobile payment link
 *
 * This function:
 * 1. Creates a Stripe Checkout Session
 * 2. Sends the payment link via SMS
 * 3. Returns the result
 */
export const sendMobilePaymentLink = async (
  request: MobilePaymentRequest,
  businessName: string = 'SLIK POS'
): Promise<MobilePaymentResponse> => {
  try {
    // Validate phone number
    if (!isValidPhoneNumber(request.customerPhone)) {
      return {
        success: false,
        error: 'Invalid phone number format',
      };
    }

    // Format phone number
    const formattedPhone = formatPhoneNumber(request.customerPhone);

    console.log(`[Mobile Payment] Creating payment link for ${request.customerName} (${formattedPhone})`);

    // Step 1: Create Stripe Checkout Session (payment link)
    const paymentLinkRequest: PaymentLinkRequest = {
      items: request.items,
      customerName: request.customerName,
      customerEmail: request.customerEmail,
      customerPhone: formattedPhone,
      metadata: {
        ...request.metadata,
        paymentType: 'mobile_link',
        originalPhone: request.customerPhone,
      },
    };

    const { checkoutUrl, sessionId } = await createPaymentLink(paymentLinkRequest);

    console.log(`[Mobile Payment] Payment link created: ${checkoutUrl}`);
    console.log(`[Mobile Payment] Session ID: ${sessionId}`);

    // Step 2: Send SMS with payment link — through the SLIK Connect queue (threaded + delivery-tracked).
    const message = createPaymentMessage(
      businessName,
      request.customerName,
      request.total,
      checkoutUrl
    );

    const queued = await communicationService.sendToClient({
      clientId: request.metadata?.customerId || undefined,
      clientName: request.customerName || formattedPhone,
      phone: formattedPhone,
      appointmentId: request.metadata?.appointmentId || undefined,
      source: 'payment_link',
      body: message,
    });

    if (!queued) {
      // Queue write failed (offline + no SDK buffer) — the link still exists; fall back to the direct
      // sender so a text can still go out, and surface the link for manual share either way.
      console.warn('[Mobile Payment] Connect queue failed; attempting direct send fallback');
      const smsResult = await sendSMS(formattedPhone, message);
      return {
        success: true, // Link still exists
        paymentLink: checkoutUrl,
        sessionId,
        smsSent: smsResult.success,
        ...(smsResult.success ? {} : { error: `Payment link created but SMS failed: ${smsResult.error}. Please share the link manually: ${checkoutUrl}` }),
      };
    }

    console.log('[Mobile Payment] Payment link queued + threaded in SLIK Connect');

    return {
      success: true,
      paymentLink: checkoutUrl,
      sessionId,
      smsSent: true,
    };

  } catch (error: any) {
    console.error('[Mobile Payment] Error:', error);
    return {
      success: false,
      error: error.message || 'Failed to create mobile payment link',
    };
  }
};

/**
 * Check payment status by session ID
 *
 * This can be used to verify if the customer has completed payment
 * Requires a backend endpoint to call Stripe's API
 */
export const checkPaymentStatus = async (
  sessionId: string
): Promise<{ paid: boolean; amount?: number; error?: string }> => {
  try {
    // TODO: Call backend to check Stripe Checkout Session status
    const response = await fetch(`/api/check-payment-status/${sessionId}`);

    if (!response.ok) {
      throw new Error('Failed to check payment status');
    }

    const data = await response.json();
    return {
      paid: data.status === 'complete',
      amount: data.amount_total ? data.amount_total / 100 : undefined,
    };
  } catch (error: any) {
    console.error('Error checking payment status:', error);
    return {
      paid: false,
      error: error.message || 'Failed to check payment status',
    };
  }
};

/**
 * Generate a short link (optional enhancement)
 * Useful for making SMS messages shorter
 *
 * Services like Bitly can be used for this
 */
export const shortenPaymentLink = async (longUrl: string): Promise<string> => {
  try {
    // TODO: Integrate with link shortening service
    // For now, return the original URL
    return longUrl;
  } catch (error) {
    console.error('Error shortening link:', error);
    return longUrl; // Fallback to original URL
  }
};

/**
 * Create a QR code for the payment link
 * Customer can scan instead of clicking SMS link
 * Returns a data URL that can be displayed as an image
 */
export const createPaymentQRCode = async (paymentLink: string): Promise<string> => {
  try {
    // Using a public QR code API (you could also use a library like qrcode)
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(paymentLink)}`;
    return qrCodeUrl;
  } catch (error) {
    console.error('Error creating QR code:', error);
    throw error;
  }
};

// Export types
export type { PaymentLinkRequest } from './stripePaymentService';
